/**
 * Once-per-session identity work for the signed-in person: reconcile this
 * device's push subscription with the backend, and identify them to
 * RevenueCat so a purchase is filed against the right account.
 *
 * Both used to run only on SIGNED_IN — which auth-js never emits on a cold
 * launch with a restored session (that is INITIAL_SESSION, and useAuth reads
 * it through getSession()). So the everyday case — open the app, already
 * signed in — never re-synced push (a rotated / wrongly-flagged subscription
 * stayed broken) and never identified RevenueCat (a purchase could land on an
 * anonymous RevenueCat id) (review 2026-10-17).
 *
 * Module-level on purpose: useAuth() is mounted by many components, each with
 * its own listener, so a per-hook ref would fire this N times. sign-out calls
 * forgetSessionIdentity() so the next sign-in (even by the same person, whose
 * push was just unregistered) runs it again.
 */

let syncedFor: string | null = null

export interface SessionIdentityDeps {
  syncPush: () => Promise<void>
  revenuecatLogIn: (userId: string) => Promise<unknown>
}

/** Run the identity work for `userId` unless it already ran for them in this
 *  page load. Returns true when it ran. Never throws. */
export function syncSessionIdentity(userId: string | null | undefined, deps: SessionIdentityDeps): boolean {
  if (!userId || syncedFor === userId) return false
  syncedFor = userId
  try { deps.syncPush().catch(() => {}) } catch { /* best-effort */ }
  try { deps.revenuecatLogIn(userId).catch(() => {}) } catch { /* best-effort */ }
  return true
}

export function forgetSessionIdentity(): void {
  syncedFor = null
}
